import type { Column, SortDescriptor } from '../../Table';
import type { WorkItemsTab } from '../types';

/** The raw `work_items` row shape fetched by `useWorkItems`. */
export interface WorkItemRow {
  id: string;
  name: string;
  type: string;
  status: string;
  rank: number;
  parent_work_item_id: string | null;
  start_date: string | null;
  end_date: string | null;
  due_date: string | null;
  created_at: string | null;
  created_by: string | null;
  updated_at: string | null;
  assignee_id: string | null;
  priority: string | null;
}

export const PRIORITY_CHOICES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' }
];

// All columns surfaced in the column picker.  Default-visible are listed
// first so the picker reads top-down in priority order; the keys here are the
// stable identifiers that round-trip in the URL `?cols=` param.
//
// This module-level shape is the static skeleton used for column-key ordering
// and the column picker. The runtime columns passed to <Table> are composed
// inside WorkItemsTableTab via useMemo so we can inject `options` for the
// editable single_select / person editors based on async-loaded data.
export const WORK_ITEM_COLUMNS: Column[] = [
  { key: 'name',          label: 'Title',         type: 'text', width: 'var(--table-col-w-xl)', editable: false },
  { key: 'type',          label: 'Type',          type: 'text', width: 'var(--table-col-w-sm)', editable: false },
  // Status needs room for the widest pill ("In progress"); Due needs a full
  // mm/dd/yyyy — both bumped from `sm` (120px) to `md` (160px).
  { key: 'status',        label: 'Status',        type: 'text', width: 'var(--table-col-w-md)', editable: true },
  { key: 'assignee',      label: 'Assignee',      type: 'text', width: 'var(--table-col-w-sm)', editable: true },
  { key: 'priority',      label: 'Priority',      type: 'text', width: 'var(--table-col-w-sm)', editable: true },
  { key: 'due',           label: 'Due',           type: 'date', width: 'var(--table-col-w-md)', editable: true },
  { key: 'last_event_at', label: 'Last activity', type: 'text', width: 'var(--table-col-w-md)', editable: false },
  // For non-Epic items, parent IS the Epic (per the
  // work_items_enforce_parent_type DB trigger). The column key was renamed
  // from `parent` to `epic` when Epics became first-class on /work-items.
  // Legacy `?cols=parent` URLs are migrated by applyColAliases.
  { key: 'epic',          label: 'Epic',          type: 'text', width: 'var(--table-col-w-lg)', editable: true },
  { key: 'created_by',    label: 'Created by',    type: 'text', width: 'var(--table-col-w-md)', editable: false },
  { key: 'created_at',    label: 'Created',       type: 'date', width: 'var(--table-col-w-sm)', editable: false },
  { key: 'rank',          label: 'Rank',          type: 'text', width: 'var(--table-col-w-xs)', editable: false }
];

export const DEFAULT_VISIBLE_COLS: ReadonlyArray<string> = [
  'name',
  'type',
  'status',
  'assignee',
  'due',
  'last_event_at'
];

// localStorage fallback for per-user persistence.  TODO follow-up: move to a
// dedicated `user_view_settings` Supabase table so the choice round-trips
// across browsers + devices for the same user.
export const COLS_STORAGE_KEY = 'penrose:work-items:cols';

// Title (`name`) is structurally required: every row needs a clickable title to
// open detail, so the picker locks it on (matches web-mock/features/work-items/).
export const LOCKED_COLS: ReadonlyArray<string> = ['name'];

export const TABLE_DEFAULT_SORT: SortDescriptor = {
  columnKey: 'name',
  direction: 'asc'
};

export const STATUS_CHOICES: ReadonlyArray<string> = [
  'Backlog',
  'In progress',
  'Review',
  'Done'
];

// `?cols=...parent...` URLs from before the rename keep working: any reference
// to the legacy `parent` key is treated as the new `epic` key.
const LEGACY_COL_ALIASES: Readonly<Record<string, string>> = {
  parent: 'epic'
};

export function applyColAliases(cols: ReadonlyArray<string>): string[] {
  return cols.map((c) => LEGACY_COL_ALIASES[c] ?? c);
}

export const TAB_ORDER: ReadonlyArray<WorkItemsTab> = ['board', 'table', 'timeline'];

export const TAB_LABELS: Readonly<Record<WorkItemsTab, string>> = {
  board: 'Board',
  table: 'Table',
  timeline: 'Timeline'
};

export function parseTab(raw: string | null): WorkItemsTab {
  // Legacy `?tab=list` URLs land on Table — the merged view that replaced List.
  if (raw === 'list') return 'table';
  if (raw === 'table' || raw === 'timeline') return raw;
  return 'board';
}
